import React from 'react'
import {
  Grid,
  Typography,
  Box,
  IconButton,
  SideDrawerWrapper,
} from '@the-platform-group/component-library'
import { Edit as EditIcon, Delete as DeleteIcon } from '@the-platform-group/component-library/Icons'
import { Modal } from '@the-platform-group/component-library/Modal'
import { DataGrid } from '@the-platform-group/component-library/DataGrid'
import { DATETIME } from '@the-platform-group/component-library/DataGrid/constant'
import { Button } from '@the-platform-group/component-library/Button'
import { TruncatedText } from 'components/TruncatedText'

import { getFullName } from '@the-platform-group/formatters/people'

import useCustomerRequestInstructions from './hooks/useCustomerRequestInstructions'

import FilesCell from '../Requests/components/FilesCell'
import { bulkDownloadCustomerUploadInstructionUrl } from 'services/customerUploadInstructions'

import CustomerRequestInstructionsModalForm from './CustomerRequestInstructionsModalForm'
import CustomerRequestInstructionsModalDelete from './CustomerRequestInstructionModalDelete'
import useCustomerRequestInstructionsModalState from './hooks/useCustomerRequestInstructionsModalState'

const ActionsButtons = ({ row, handleModalOpen }) => {
  return (
    <>
      <IconButton size="small" onClick={() => handleModalOpen('edit')(row)}>
        <EditIcon />
      </IconButton>
      <IconButton size="small" onClick={() => handleModalOpen('delete')(row)}>
        <DeleteIcon />
      </IconButton>
    </>
  )
}

const CustomerRequestInstructions = ({ customer }) => {
  const {
    customerInstructions,
    isFetchingCustomerInstructions,
    handleInstructionDelete,
    handleFileDownload,
  } = useCustomerRequestInstructions(customer)

  const { handleModalOpen, handleModalClose, createModalState, editModalState, deleteModalState } =
    useCustomerRequestInstructionsModalState(customerInstructions)

  // the create and edit form look almost identical.
  // the only difference is if the form is pre-populated
  const { open: createModalOpen } = createModalState
  const { open: editModalOpen, instructionToEdit } = editModalState
  const { open: deleteModalOpen, instructionToDelete } = deleteModalState

  const upsertModalFormOpen = createModalOpen || editModalOpen

  const handleClose = reset => {
    handleModalClose()
    if (typeof reset === 'function') {
      reset()
    }
  }

  return (
    <SideDrawerWrapper headerProps={{ sx: { pl: 2.5, pb: 0 } }}>
      <Grid container justifyContent="space-between" alignItems="center">
        <Grid item>
          <Typography variant="h6">Irwin Assistant Instructions</Typography>
        </Grid>
        <Grid item>
          <Button onClick={handleModalOpen('create')}>Add Instructions</Button>
        </Grid>
        <Grid item xs={12} sx={{ my: 1 }}>
          <DataGrid
            disableColumnFilter
            disableColumnReorder
            disableColumnPinning
            disableExport
            disableDensitySelector
            disableColumnMenu
            disableColumnSelector
            disableMultipleColumnsSorting
            disableReset
            pagination={false}
            autoHeight={false}
            getRowHeight={obj => obj.densityFactor * 90}
            rows={customerInstructions}
            localeText={{
              noRowsLabel:
                'No instructions have been added yet. You can add any client-specific details here when available.',
            }}
            columns={[
              {
                field: 'instructions',
                renderCell: ({ value }) => (
                  <Box width="100%">
                    <TruncatedText title="" content={value} contentPreview={value} maxLines={2} />
                  </Box>
                ),
              },
              {
                field: 'attachments',
                renderCell: ({ row }) => {
                  const value = { ...row, name: row.customer.name }

                  return (
                    <FilesCell
                      value={value || {}}
                      handleFileDownload={handleFileDownload}
                      getBulkDownloadUrl={bulkDownloadCustomerUploadInstructionUrl}
                    />
                  )
                },
              },
              { field: 'updatedAt', type: DATETIME },
              {
                field: 'updatedBy',
                valueGetter: ({ row }) => getFullName(row.creator),
              },
              {
                field: 'actions',
                type: 'actions',
                getActions: ({ row }) => [
                  <ActionsButtons row={row} handleModalOpen={handleModalOpen} />,
                ],
              },
            ]}
            name="customerRequestInstructions"
            loading={isFetchingCustomerInstructions}
          />
        </Grid>
        <Modal open={upsertModalFormOpen} onClose={handleClose} sx={{ m: 3 }}>
          <CustomerRequestInstructionsModalForm
            customer={customer}
            handleClose={handleClose}
            instructionsToEdit={instructionToEdit}
          />
        </Modal>
        <Modal open={deleteModalOpen} onClose={handleClose} sx={{ m: 3 }}>
          <CustomerRequestInstructionsModalDelete
            customer={customer}
            open={deleteModalOpen}
            handleClose={handleClose}
            instructionToDelete={instructionToDelete}
            handleInstructionDelete={handleInstructionDelete}
          />
        </Modal>
      </Grid>
    </SideDrawerWrapper>
  )
}

export default CustomerRequestInstructions
